import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { BULK_TIERS, hasBulkPricing, minUnitPrice } from '../../util/bulkPricing';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  IconReviewStar,
} from '../../components';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const formatRating = rating => Number(rating.toFixed(2)).toString();

const { Money } = sdkTypes;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, shopId, config, intl, listingTypeConfig, ratingData } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  // Volume-priced gourmet sets (see util/bulkPricing): the card shows the lowest per-set price
  // ("From $54") and the regular price in the tooltip.
  const bulkKey = { category: publicData?.categoryLevel1, shopId };
  const bulk = price && hasBulkPricing(bulkKey);
  const shownPrice = bulk ? new Money(minUnitPrice(price.amount, bulkKey), price.currency) : price;
  const { formattedPrice, priceTitle: basePriceTitle } = priceData(shownPrice, config.currency, intl);
  const priceTitle = bulk
    ? `${formatMoney(intl, price)} each · ${formattedPrice} each when ordering ${
        BULK_TIERS[BULK_TIERS.length - 1].minQty
      }+`
    : basePriceTitle;

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <>
          <FormattedMessage
            id={bulk ? 'ListingCard.priceStartingFrom' : 'ListingCard.price'}
            values={{ priceValue, pricePerUnit }}
          />
          {ratingData ? (
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
              <IconReviewStar className={css.starIcon} isFilled />
              <span style={{ marginLeft: '2px' }}>{formatRating(ratingData.rating)} </span>
              <span style={{ marginLeft: '2px' }}>({ratingData.reviewCount})</span>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
// Distance from the shop to the customer's saved address (topbar), in km.
const distanceKm = (a, b) => {
  const toRad = d => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const useSavedAddressPoint = () => {
  const [point, setPoint] = useState(null);
  useEffect(() => {
    const sync = () =>
      import('../../util/savedAddress').then(m => {
        const saved = m.getSavedAddress();
        setPoint(saved?.lat != null ? { lat: saved.lat, lng: saved.lng } : null);
      });
    sync();
    window.addEventListener('saved-address-changed', sync);
    return () => window.removeEventListener('saved-address-changed', sync);
  }, []);
  return point;
};

const ListingCardImage = props => {
  const {
    currentListing,
    hasVideo,
    fastDeliveryHours,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
      {fastDeliveryHours ? (
        <span className={css.fastDeliveryBadge}>
          <FormattedMessage id="ListingCard.fastDeliveryBadge" values={{ hours: fastDeliveryHours }} />
        </span>
      ) : null}
      {hasVideo ? (
        <span className={css.videoBadge}>
          <span className={css.videoBadgeIcon} />
          <FormattedMessage id="ListingCard.videoBadge" />
        </span>
      ) : null}
    </AspectRatioWrapper>
  ) : (
    <div className={css.thumbnailWrapper}>
      <ListingCardThumbnail
        style={style}
        listingTitle={title}
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        setActivePropsMaybe={setActivePropsMaybe}
      />
      {fastDeliveryHours ? (
        <span className={css.fastDeliveryBadge}>
          <FormattedMessage id="ListingCard.fastDeliveryBadge" values={{ hours: fastDeliveryHours }} />
        </span>
      ) : null}
      {hasVideo ? (
        <span className={css.videoBadge}>
          <span className={css.videoBadgeIcon} />
          <FormattedMessage id="ListingCard.videoBadge" />
        </span>
      ) : null}
    </div>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const savedPoint = useSavedAddressPoint();
  const geolocation = currentListing.attributes.geolocation;
  const kmAway =
    savedPoint && geolocation?.lat != null
      ? Math.round(distanceKm(geolocation, savedPoint) * 10) / 10
      : null;
  const outOfZone = currentListing.attributes.metadata?.outOfDeliveryZone === true;
  const soldOut = currentListing.attributes.metadata?.soldOut === true;

  const { listingType, cardStyle } = publicData || {};
  const listingRatingData = publicData?.aggregateRating
    ? {
        rating: publicData.aggregateRating / 100,
        reviewCount: publicData.numberOfRatings,
      }
    : null;
  // Rating comes from the shop's live reviews (mig_reviews), which the search
  // API now bakes into publicData.aggregateRating. (The old static
  // manualStoreReviews are being migrated into mig_reviews, so we no longer add
  // them here — that would double-count once migrated.)
  const ratingData = listingRatingData;
  const hasVideo = Array.isArray(publicData?.videos) && publicData.videos.length > 0;
  const readyForSale = publicData?.['ready_for_sale'];
  // Delivery badge from preparation time: 0h prep → "1-hour delivery",
  // 1h → "2-hour", 2h → "3-hour"; longer (or unset) prep shows nothing.
  // Legacy items without an explicit preparationHours stay badge-less unless
  // they were marked ready for sale.
  const rawPrep = publicData?.preparationHours;
  const hasPrep = rawPrep !== undefined && rawPrep !== null && rawPrep !== '';
  const effPrep = readyForSale === 'yes' ? 0 : hasPrep ? Number(rawPrep) || 0 : null;
  const fastDeliveryHours = effPrep !== null && effPrep <= 2 ? effPrep + 1 : null;
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <div className={outOfZone || soldOut ? css.dimmedBlock : undefined}>
        <ListingCardImage
          renderSizes={renderSizes}
          title={title}
          currentListing={currentListing}
          hasVideo={hasVideo}
          fastDeliveryHours={fastDeliveryHours}
          config={config}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          style={cardStyle}
          showListingImage={showListingImage}
        />
      </div>
      {soldOut ? (
        <div className={css.outOfZoneNote}>Sold out</div>
      ) : outOfZone ? (
        <div className={css.outOfZoneNote}>Doesn’t deliver to your address</div>
      ) : null}
      <div className={outOfZone ? classNames(css.info, css.dimmedBlock) : css.info}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          shopId={author.id?.uuid}
          config={config}
          intl={intl}
          listingTypeConfig={foundListingTypeConfig}
          ratingData={ratingData}
        />
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
          )}
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              {kmAway != null ? <span className={css.distanceAway}> · {kmAway} km</span> : null}
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
